import React, { useState, useEffect } from 'react';
import { Users, UserCheck, UserX, Shield, Edit2, StopCircle, PlayCircle, Building2, Key, Search } from 'lucide-react';
import Table from '../Table/Table';
import CommonPagination from '../Common/CommonPagination';
import Loading from '../Common/Loading';
import OrganisationModal from './OrganisationModal';
import ChangePasswordModal from '../UserPermissionManagement/UserManagement/ChangePasswordModal';
import { Tooltip } from "../Common/Tooltip";
import ConfirmModal from '../Common/ConfirmModal';
import Toast, { ToastType } from '../Common/Toast';
import organisationService, { Organisation, OrganisationStats } from '../../services/organisation.service';
import styles from './OrganisationManagement.module.css';
import { jwtDecode } from "jwt-decode";
import authService from "../../services/auth.service";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from '../../utils/api';
const OrganisationManagement: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [organisations, setOrganisations] = useState<Organisation[]>([]);
    const [stats, setStats] = useState<OrganisationStats | null>(null);
    const [totalOrganisations, setTotalOrganisations] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingOrg, setEditingOrg] = useState<Organisation | null>(null);
    const [changePasswordOrg, setChangePasswordOrg] = useState<Organisation | null>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    // Search State
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isToggleLoading, setIsToggleLoading] = useState(false);

    const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; org: Organisation | null; action: 'toggle' }>({ isOpen: false, org: null, action: 'toggle' });
    const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const navigate = useNavigate();

    const token = authService.getToken();
    const payload: any = token ? jwtDecode(token) : null;


    // Debounce Search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(0); // Reset to first page on search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (!authService.getToken()) return;  // important

        loadData();
    }, [currentPage, itemsPerPage, statusFilter, debouncedSearch]);

    const loadData = async () => {
        const token = authService.getToken();
        if (!token) return;
        try {
            setLoading(true);
            const [orgsData, statsData] = await Promise.all([
                organisationService.getOrganisations(currentPage + 1, itemsPerPage, debouncedSearch, statusFilter || undefined),
                organisationService.getOrganisationStats()
            ]);
            setOrganisations(orgsData.organisations);
            setTotalOrganisations(orgsData.total);
            setStats(statsData);
        } catch (error) {
            console.error('Failed to load organisations:', error);
            setToast({ message: 'Failed to load organisations', type: 'error' });
        } finally {
            setLoading(false);
            setIsInitialLoading(false);
        }
    };
    const handleSelectOrganisation = async (org_id: string) => {
        try {
            setLoading(true);
            setIsInitialLoading(false);
            const data = await organisationService.selectOrganisation(org_id);

            // Save new tokens
            authService.saveToken(data.access_token);
            authService.saveRefreshToken(data.refresh_token);

            // NOW fetch fresh user from backend
            const response = await fetchWithAuth("/api/users/me", {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });

            if (!response.ok) {
                throw new Error("Failed to fetch user after org select");
            }

            const freshUser = await response.json();

            // Save full updated user (roles, permissions, org, everything)
            authService.saveUser(freshUser);

            // HARD reload to reset app state completely - always to dashboard when explicitly selecting an org
            window.location.href = "/dashboard";

        } catch (err) {
            setLoading(false);
            console.error("Select organisation failed", err);
        }
    };



    const handleEdit = async (org: Organisation) => {
        try {
            setLoading(true);

            const fullOrg = await organisationService.getOrganisationById(org.id);

            setEditingOrg(fullOrg);
            setIsModalOpen(true);
        } catch (error: any) {
            console.error("Failed to load organisation details:", error);
            setToast({
                message: error.message || "Failed to load organisation details",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = (org: Organisation) => {
        setChangePasswordOrg(org);
    };

    const handlePasswordSubmit = async (password: string) => {
        if (!changePasswordOrg) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/organisations/${changePasswordOrg.id}/change-password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify({ new_password: password })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to change password');
            }

            setToast({ message: 'Password changed successfully', type: 'success' });
            setChangePasswordOrg(null);
        } catch (error: any) {
            console.error('Failed to change password:', error);
            setToast({ message: error.message || 'Failed to change password', type: 'error' });
            throw error;
        }
    };

    const handleAddNew = () => {
        setEditingOrg(null);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingOrg(null);
    };

    const handleModalSubmit = async (data: any) => {
        try {
            if (editingOrg) {
                await organisationService.updateOrganisation(editingOrg.id, data);
                setToast({ message: 'Organisation updated successfully', type: 'success' });
            } else {
                await organisationService.createOrganisation(data);
                setToast({ message: 'Organisation created successfully', type: 'success' });
            }
            handleModalClose();
            loadData();
        } catch (error: any) {
            console.error('Failed to save organisation:', error);
            const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save organisation';
            setToast({ message: errorMessage, type: 'error' });
        }
    };

    const handleToggleStatus = (org: Organisation) => {
        setConfirmModal({ isOpen: true, org, action: 'toggle' });
    };

    const handleConfirmAction = async () => {
        if (!confirmModal.org) return;

        try {
            setIsToggleLoading(true);
            const isActive = confirmModal.org.statusCode === 'ACTIVE';
            if (isActive) {
                await organisationService.deactivateOrganisation(confirmModal.org.id);
                setToast({ message: 'Organisation deactivated successfully', type: 'success' });
            } else {
                await organisationService.activateOrganisation(confirmModal.org.id);
                setToast({ message: 'Organisation activated successfully', type: 'success' });
            }
            loadData();
            setConfirmModal({ isOpen: false, org: null, action: 'toggle' });
        } catch (error: any) {
            console.error('Failed to perform action:', error);
            const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to perform action';
            setToast({ message: errorMessage, type: 'error' });
        } finally {
            setIsToggleLoading(false);
        }
    };

    const handleStatClick = (type: 'total' | 'active' | 'inactive') => {
        if (type === 'total') {
            setStatusFilter(null);
        } else if (type === 'active') {
            setStatusFilter('ACTIVE');
        } else if (type === 'inactive') {
            setStatusFilter('INACTIVE');
        }
        setCurrentPage(0);
    };

    interface StatItem {
        title: string;
        value: string;
        icon: React.ElementType;
        color: string;
        onClick?: () => void;
        active?: boolean;
    }

    const orgStats: StatItem[] = [
        {
            title: 'All Organisations',
            value: stats?.total_organisations.toString() || '0',
            icon: Building2,
            color: 'blue',
            onClick: () => handleStatClick('total'),
            active: statusFilter === null
        },
        {
            title: 'Active Organisations',
            value: stats?.active_organisations.toString() || '0',
            icon: UserCheck,
            color: 'green',
            onClick: () => handleStatClick('active'),
            active: statusFilter === 'ACTIVE'
        },
        {
            title: 'Inactive Organisations',
            value: stats?.inactive_organisations.toString() || '0',
            icon: UserX,
            color: 'red',
            onClick: () => handleStatClick('inactive'),
            active: statusFilter === 'INACTIVE'
        }
    ];

    const columns = [
        {
            key: 'name',
            header: 'Organisation Name',
            render: (_: any, row: Organisation) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 600 }}>{row.name}</span>
                </div>
            )
        },
        {
            key: 'admin_name',
            header: 'Admin Name',
            render: (_: any, row: Organisation) => {
                const fullName = [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(' ');
                return (
                    <span style={{ fontWeight: 600 }}>
                        {fullName || '-'}
                    </span>
                );
            }
        },
        {
            key: 'username',
            header: 'Username',
            render: (_: any, row: Organisation) => (
                row.username ? <span className={styles.usernameBadge}>{row.username}</span> : <span>-</span>
            )
        },
        {
            key: 'phone',
            header: 'Phone',
            render: (_: any, row: Organisation) => {
                const phone = row.phone_country_code && row.phone_number
                    ? `${row.phone_country_code}-${row.phone_number}`
                    : null;
                return phone ? (
                    <span>{phone}</span>
                ) : (
                    <span>-</span>
                );
            }
        },
        {
            key: 'email',
            header: 'Email',
            render: (value: string) => value || "-"
        },
        {
            key: 'statusCode',
            header: 'Status',
            render: (value: string | undefined) => {
                const isActive = value === 'ACTIVE';
                return (
                    <span className={`${styles.statusBadge} ${isActive ? styles.active : styles.inactive}`}>
                        {isActive ? 'Active' : 'Inactive'}
                    </span>
                );
            }
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (_: any, row: Organisation) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Tooltip content="Edit">
                        <button 
                            className={`${styles.actionBtn} ${styles.edit}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row)
                            }}
                        >
                            <Edit2 size={14} />
                        </button>
                    </Tooltip>
                    <Tooltip content="Change Password">
                        <button
                            className={`${styles.actionBtn} ${styles.key}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleChangePassword(row);
                            }}
                        >
                            <Key size={14} />
                        </button>
                    </Tooltip>
                    {row.statusCode === 'ACTIVE' && (
                        <Tooltip content="Deactivate">
                            <button
                                className={`${styles.actionBtn} ${styles.deactivate}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleStatus(row);
                                }}
                            >
                                <StopCircle size={14} />
                            </button>
                        </Tooltip>
                    )}
                    {row.statusCode !== 'ACTIVE' && (
                        <Tooltip content="Activate">
                            <button
                                className={`${styles.actionBtn} ${styles.activate}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleStatus(row);
                                }}
                            >
                                <PlayCircle size={14} />
                            </button>
                        </Tooltip>
                    )}
                </div>
            )
        }
    ];

    if (isInitialLoading) {
        return <Loading message="Loading organisations..." />;
    }

    return (
        <div className={styles.managementContent}>
            <div className={styles.statsGrid}>
                {orgStats.map((stat, index) => (
                    <div
                        key={index}
                        className={`${styles.statCard} ${styles[stat.color]} ${stat.onClick ? styles.clickable : ''} ${stat.active ? styles.selected : ''}`}
                        onClick={stat.onClick}
                        style={{ cursor: stat.onClick ? 'pointer' : 'default' }}
                    >
                        <div className={styles.statIcon}>
                            <stat.icon size={16} />
                        </div>
                        <div className={styles.statContent}>
                            <h3>{stat.value}</h3>
                            <p>{stat.title}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.tableSection} style={{ position: 'relative' }}>
                <div className={styles.tableHeader}>
                    <h2>
                        <Building2 size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Organisations
                    </h2>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
                            <input
                                type="text"
                                placeholder="Search organisations..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    paddingLeft: '32px',
                                    paddingRight: '12px',
                                    height: '36px',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    fontSize: '14px',
                                    width: '240px',
                                    outline: 'none'
                                }}
                            />
                        </div>
                        <button className={styles.addBtn} onClick={handleAddNew}>
                            Add Organisation
                        </button>
                    </div>
                </div>
                <Table
                    columns={columns}
                    data={organisations}
                    maxHeight="calc(100vh - 360px)"
                    onRowClick={(row) => handleSelectOrganisation(row.id)}
                />
                {loading && !isInitialLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 255, 255, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                    }}>
                        <Loading message="Updating..." />
                    </div>
                )}
            </div>

            <CommonPagination
                show={totalOrganisations > 0}
                pageCount={Math.ceil(totalOrganisations / itemsPerPage)}
                currentPage={currentPage}
                totalItems={totalOrganisations}
                itemsPerPage={itemsPerPage}
                onPageChange={(data) => setCurrentPage(data.selected)}
                onItemsPerPageChange={(items) => {
                    setItemsPerPage(items);
                    setCurrentPage(0);
                }}
            />
            <OrganisationModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSubmit={handleModalSubmit}
                initialData={editingOrg ? {
                    id: editingOrg.id,
                    name: editingOrg.name,
                    email: editingOrg.email,
                    username: editingOrg.username,
                    first_name: editingOrg.first_name,
                    middle_name: editingOrg.middle_name,
                    last_name: editingOrg.last_name,
                    phone_country_code: editingOrg.phone_country_code,
                    phone_number: editingOrg.phone_number
                } : undefined}
                title={editingOrg ? 'Edit Organisation' : 'Add New Organisation'}
            />

            <ChangePasswordModal
                isOpen={!!changePasswordOrg}
                onClose={() => setChangePasswordOrg(null)}
                onSubmit={handlePasswordSubmit}
                username={changePasswordOrg?.name || ''}
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, org: null, action: 'toggle' })}
                onConfirm={handleConfirmAction}
                title={confirmModal.org?.statusCode === 'ACTIVE' ? "Deactivate Organisation" : "Activate Organisation"}
                message={`Are you sure you want to ${confirmModal.org?.statusCode === 'ACTIVE' ? 'deactivate' : 'activate'} this organisation?`}
                confirmText={confirmModal.org?.statusCode === 'ACTIVE' ? "Deactivate" : "Activate"}
                type={confirmModal.org?.statusCode === 'ACTIVE' ? "warning" : "info"}
                loading={isToggleLoading}
            />

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default OrganisationManagement;
